import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { CalendarDays, Moon, ShieldOff, CheckCircle2, X as XIcon, Clock } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const parseUTC = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso);
  } catch (e) {
    return null;
  }
};

const fmtClock = (date) => {
  if (!date) return "—";
  return date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const fmtClockUTC = (date) => {
  if (!date) return "—";
  return `${String(date.getUTCHours()).padStart(2, "0")}:${String(date.getUTCMinutes()).padStart(2, "0")} UTC`;
};

const fmtDayLabel = (date) => {
  if (!date) return "—";
  const now = new Date();
  const d0 = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dt0 = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diff = Math.round((dt0 - d0) / (24 * 60 * 60 * 1000));
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  return date.toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
};

const localDateKey = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const HOURS_AHEAD_FETCH = 168; // 7 days
const DAYS_TO_RENDER = 7;
const HOUR_STEP_MIN = 30; // half-hour granularity for picker

/**
 * Calendar picker for proposing a meeting time when submitting an offer.
 *
 * Reads the public availability schedule to block sleep / unavailable hours
 * (and past times). The user can optionally pick a time — they can also
 * submit without picking anything ("No specific time").
 *
 * Props:
 *   value: ISO UTC string or null
 *   onChange: (iso | null) => void
 */
const MeetingTimePickerInline = ({ value, onChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dayOffset, setDayOffset] = useState(0);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(
          `${API_URL}/api/orestore/availability/status?hours=${HOURS_AHEAD_FETCH}`
        );
        if (!cancel) setData(r.data);
      } catch (e) {
        // tolerate — picker still works (no blockers)
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  const now = useMemo(() => new Date(), []);

  // Build a Map<dateKey, [{start, end, type}]>
  const slotsByDate = useMemo(() => {
    const slots = data?.slots || [];
    const map = new Map();
    for (const s of slots) {
      const start = parseUTC(s.start_utc);
      const end = parseUTC(s.end_utc);
      if (!start || !end) continue;
      let cursor = new Date(start);
      while (cursor < end) {
        const k = localDateKey(cursor);
        if (!map.has(k)) map.set(k, []);
        // Walk hour by hour to handle slots crossing midnight in user local TZ.
        const tomorrowStart = new Date(cursor);
        tomorrowStart.setHours(0, 0, 0, 0);
        tomorrowStart.setDate(tomorrowStart.getDate() + 1);
        const sliceEnd = end < tomorrowStart ? end : tomorrowStart;
        map.get(k).push({ start: new Date(cursor), end: new Date(sliceEnd), type: s.type });
        cursor = sliceEnd;
      }
    }
    return map;
  }, [data]);

  const baseDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const selectedDay = new Date(baseDate);
  selectedDay.setDate(selectedDay.getDate() + dayOffset);
  const dayKey = localDateKey(selectedDay);

  // Build half-hour grid (08:00 .. 23:30) — outside this range trades are unlikely.
  const cells = [];
  const dayStart = new Date(selectedDay);
  dayStart.setHours(0, 0, 0, 0);
  for (let m = 0; m < 24 * 60; m += HOUR_STEP_MIN) {
    const cellStart = new Date(dayStart.getTime() + m * 60 * 1000);
    const cellEnd = new Date(cellStart.getTime() + HOUR_STEP_MIN * 60 * 1000);
    let blocker = null;
    let isAvailable = false;
    const daySlots = slotsByDate.get(dayKey) || [];
    for (const s of daySlots) {
      if (s.start < cellEnd && s.end > cellStart) {
        if (s.type === "sleep" || s.type === "unavailable") {
          blocker = s.type;
          break;
        }
        if (s.type === "available") isAvailable = true;
      }
    }
    const isPast = cellEnd <= now;
    cells.push({ cellStart, cellEnd, blocker, isAvailable, isPast });
  }

  // Selected ISO -> Date
  const selectedDate = value ? new Date(value) : null;

  const selectCell = (cellStart) => {
    onChange(cellStart.toISOString());
  };
  const clearSelection = () => onChange(null);

  // For selected time display — show both local and UTC
  let selectedLocalLabel = null;
  let selectedUTCLabel = null;
  if (selectedDate) {
    selectedLocalLabel = `${fmtDayLabel(selectedDate)} ${fmtClock(selectedDate)}`;
    selectedUTCLabel = `${selectedDate.toLocaleDateString(undefined, {
      weekday: "short",
      day: "numeric",
      month: "short",
      timeZone: "UTC",
    })} ${fmtClockUTC(selectedDate)}`;
  }

  if (loading) {
    return (
      <div className="text-[12px] text-orange-200/70 py-3">
        Loading owner's availability…
      </div>
    );
  }

  return (
    <div
      className="rounded-lg border-2 border-[#5a3a14] bg-[#1f1208] p-3 space-y-2"
      data-testid="meeting-time-picker"
    >
      <div className="flex items-center gap-2">
        <CalendarDays className="w-4 h-4 text-amber-300" />
        <div className="font-extrabold uppercase tracking-wider text-amber-200 text-sm">
          Propose a meeting time (optional)
        </div>
      </div>
      <p className="text-[11px] text-orange-200/70">
        Pick a half-hour slot when you'd like to meet the seller. Greyed-out
        cells are sleep, unavailable, or past — these will be auto-skipped.
        Times are stored in UTC and translated to the seller's timezone when we
        notify them.
      </p>

      {/* Day tabs */}
      <div className="flex flex-wrap gap-1">
        {Array.from({ length: DAYS_TO_RENDER }).map((_, i) => {
          const d = new Date(baseDate);
          d.setDate(d.getDate() + i);
          const isSel = i === dayOffset;
          return (
            <button
              type="button"
              key={i}
              onClick={() => setDayOffset(i)}
              className={`px-2.5 py-1 text-[11px] font-bold rounded border transition ${
                isSel
                  ? "border-amber-400 bg-amber-700/50 text-amber-100"
                  : "border-[#5a3a14] bg-[#2a180a] text-orange-200/80 hover:border-amber-400"
              }`}
              data-testid={`meet-day-${i}`}
            >
              {fmtDayLabel(d)}
            </button>
          );
        })}
      </div>

      {/* Half-hour grid (24h × 2 cols of 30 min) */}
      <div className="grid grid-cols-6 sm:grid-cols-8 gap-1">
        {cells.map((c, idx) => {
          const k = c.cellStart.toISOString();
          const isSel =
            selectedDate &&
            selectedDate.getTime() === c.cellStart.getTime();
          const disabled = c.isPast || !!c.blocker;
          let bg = "bg-[#2a180a] hover:bg-[#3a2814] text-orange-200/90";
          if (c.blocker === "sleep") {
            bg =
              "bg-indigo-900/50 text-indigo-300/40 cursor-not-allowed";
          } else if (c.blocker === "unavailable") {
            bg =
              "bg-red-900/50 text-red-300/40 cursor-not-allowed";
          } else if (c.isPast) {
            bg = "bg-black/30 text-orange-200/30 cursor-not-allowed";
          } else if (c.isAvailable) {
            bg = "bg-emerald-700/40 hover:bg-emerald-600/50 text-emerald-100";
          }
          if (isSel) bg = "bg-amber-500 text-amber-950 font-extrabold";
          return (
            <button
              type="button"
              key={k}
              disabled={disabled}
              onClick={() => selectCell(c.cellStart)}
              className={`px-1 py-1 text-[10px] font-mono rounded border border-[#5a3a14] transition ${bg}`}
              title={
                c.blocker === "sleep"
                  ? "Seller is sleeping — pick another slot"
                  : c.blocker === "unavailable"
                  ? "Seller is unavailable"
                  : c.isPast
                  ? "Past"
                  : fmtClock(c.cellStart)
              }
              data-testid={`meet-cell-${dayOffset}-${idx}`}
            >
              {fmtClock(c.cellStart)}
            </button>
          );
        })}
      </div>

      {/* Legend + selected display */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-orange-200/80 pt-1">
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-sm bg-emerald-700/60" />
          <CheckCircle2 className="w-3 h-3 text-emerald-300" /> Owner usually here
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-sm bg-indigo-900/70" />
          <Moon className="w-3 h-3 text-indigo-300" /> Sleeping
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-sm bg-red-900/70" />
          <ShieldOff className="w-3 h-3 text-red-300" /> Unavailable
        </span>
      </div>

      <div
        className="flex items-center gap-2 mt-1 px-2 py-1.5 rounded border border-amber-700/40 bg-amber-900/15 text-[12px]"
        data-testid="meet-selected-display"
      >
        <Clock className="w-3.5 h-3.5 text-amber-300 flex-shrink-0" />
        {selectedDate ? (
          <>
            <div className="flex-1 min-w-0">
              <div className="font-extrabold text-amber-100">
                {selectedLocalLabel}
              </div>
              <div className="text-[10px] text-amber-200/70 font-mono">
                {selectedUTCLabel}
              </div>
            </div>
            <button
              type="button"
              onClick={clearSelection}
              className="px-1.5 py-0.5 rounded text-amber-200 hover:bg-amber-700/30 text-[11px] flex items-center gap-0.5"
              data-testid="meet-clear"
            >
              <XIcon className="w-3.5 h-3.5" /> Clear
            </button>
          </>
        ) : (
          <span className="text-amber-200/70 italic flex-1">
            No specific time selected — the seller will reach out to arrange.
          </span>
        )}
      </div>
    </div>
  );
};

export default MeetingTimePickerInline;
